/**
 * usePlan — tiny shared hook for the current tenant's plan.
 *
 * Single in-memory cache + subscriber list so every component (AI button,
 * upgrade modals, locked feature cards) sees the same value without each
 * one re-fetching /api/tenant-settings/plan on mount.
 *
 * Anything else that mutates the plan (e.g. upgrade flow) should call
 * refreshPlan() to push the new value to all subscribers.
 */
import { useEffect, useState, useCallback } from 'react';

let cache = null;          // { plan: string }
let inflight = null;       // Promise during fetch
const subs = new Set();    // setState subscribers

const broadcast = () => subs.forEach((s) => s(cache));

const PRO_PLANS = new Set(['professional', 'pro', 'enterprise']);

const fetchPlan = async () => {
  if (inflight) return inflight;
  const token = localStorage.getItem('equipr_token');
  if (!token) {
    cache = { plan: null };
    broadcast();
    return cache;
  }
  inflight = (async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant-settings/plan`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        cache = { plan: (data.plan || 'free').toLowerCase() };
      } else {
        cache = { plan: 'free' };
      }
    } catch {
      cache = { plan: 'free' };
    } finally {
      inflight = null;
    }
    broadcast();
    return cache;
  })();
  return inflight;
};

export const refreshPlan = () => {
  cache = null;
  return fetchPlan();
};

export const usePlan = () => {
  const [value, setValue] = useState(cache);

  useEffect(() => {
    subs.add(setValue);
    if (!cache && !inflight) fetchPlan();
    else if (cache) setValue(cache);
    return () => subs.delete(setValue);
  }, []);

  const plan = value?.plan ?? null;
  const isPro = plan ? PRO_PLANS.has(plan) : false;
  const loading = value === null;

  const onUpgrade = useCallback(() => {
    window.location.href = '/settings/billing';
  }, []);

  return { plan, isPro, loading, onUpgrade };
};

export default usePlan;
